import React, { useState, useEffect, useCallback } from "react";
import { auth, db, storage } from "../firebase-config";
import { awardRequestPoint, subscribeRewards } from "../services/pointsService";
import { backfillGenre } from "../services/genreService";
import { cleanYoutubeMeta } from "../services/metadataClean";
import { QRCodeSVG } from "qrcode.react";
import PrizeArchive from "./PrizeArchive";
import { subscribePrizeArchive } from "../services/prizeArchiveService";
import { signOut } from "firebase/auth";
import { doc, updateDoc, serverTimestamp, collection, query, where, onSnapshot, orderBy, getDoc, addDoc, setDoc, getDocs } from "firebase/firestore";
import useVideos from '../hooks/useVideos';
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import Cookies from "universal-cookie";
import { FileUploader } from "./FileUploader";
import LinkToGoogle from "./LinkToGoogle";
import ConversationEngine from "./ConversationEngine";
import EventGallery from "./EventGallery";
import cn from "classnames";
import { useToast } from "../context/ToastContext";

const cookies = new Cookies();

const RequestItem = ({ request, index }) => {
  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  return (
    <li className="flex items-center py-4 px-0 request-divider">
      <span className="text-gray-400 text-lg font-medium mr-2">
        {index + 1}
      </span>
      <img
        className="rounded-full object-cover mr-4"
        src={request.coverart || "/default-cover.jpg"}
        style={{ width: "48px", height: "48px" }}
        alt={request.track || "Track"}
      />
      <div className="flex-1 text-left">
        <h4 className="text-xl font-bold text-white overflow-hidden">
          {request.track || "Unknown Track"}
        </h4>
        <p className="text-lg artist-text">
          {request.artist || "Unknown Artist"}
        </p>
        <p className="text-sm request-status">
          {request.status === "open" ? (
            <span className="status-open">● Pending</span>
          ) : (
            <span className="status-closed">✓ Processed</span>
          )}
          <span className="request-date"> · {formatDate(request.createdAt)}</span>
        </p>
      </div>
    </li>
  );
};

const AccordionItem = ({
  title,
  icon,
  isOpen,
  onClick,
  children,
  isLogout = false,
  sectionId = "",
}) => {
  const contentHeight = React.useRef();
  
  return (
    <div className={cn("accordion-wrapper", { "logout-item": isLogout })}>
      <div
        className={cn("accordion-header", { active: isOpen })}
        onClick={onClick}
      >
        <div className="accordion-title-group">
          <span className="accordion-icon">{icon}</span>
          <h4 className="accordion-title">{title}</h4>
        </div>
        {!isLogout && (
          <svg
            className={cn("accordion-arrow", { active: isOpen })}
            width="20px"
            height="20px"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9 18L15 12L9 6"
              stroke="#ffffff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>
      {!isLogout && (
        <div
          ref={contentHeight}
          className={cn("accordion-content", { 
            active: isOpen,
            "dating-section": sectionId === "dating" 
          })}
        >
          <div className="accordion-content-inner">{children}</div>
        </div>
      )}
    </div>
  );
};

// Loyalty reward tiers. Thresholds (100/500/1000) are fixed by the points spec;
// the reward labels are the Brooklyn Bar launch template — per-venue reward
// menus come in a later phase.
const REWARD_TIERS = [
  { tier: 1, points: 100, reward: "3 drinks on us" },
  { tier: 2, points: 500, reward: "Dinner + drinks" },
  { tier: 3, points: 1000, reward: "VIP — hotel stay + spa day" },
];

// Tier ladder + progress-to-next-reward, shown under the points badge.
// `tiers` comes from the venue's config (falls back to the default template).
// `redeemed` = tier numbers already claimed (badges); points are never deducted.
const TierProgress = ({ points = 0, tiers = REWARD_TIERS, redeemed = [] }) => {
  const p = Number(points) || 0;
  const reached = tiers.filter((t) => p >= t.points).length;
  const next = tiers[reached]; // undefined once maxed
  const prevPoints = reached > 0 ? tiers[reached - 1].points : 0;
  const span = next ? next.points - prevPoints : 1;
  const pct = next ? Math.min(100, Math.max(0, ((p - prevPoints) / span) * 100)) : 100;
  const remaining = next ? next.points - p : 0;

  return (
    <div style={{ margin: "0 16px 14px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 16, padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10, gap: 10 }}>
        <span style={{ fontWeight: 700, color: "#fff", fontSize: "1.05rem" }}>
          {next ? `${remaining.toLocaleString()} pts to ${next.reward}` : "🎉 Top tier unlocked"}
        </span>
        <span style={{ fontSize: ".9rem", color: "#9AA0C0", whiteSpace: "nowrap" }}>
          {reached}/{tiers.length} tiers
        </span>
      </div>
      <div style={{ height: 8, borderRadius: 999, background: "rgba(255,255,255,0.1)", overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, borderRadius: 999, background: "linear-gradient(120deg,#E91E8C,#7C3AED)", transition: "width .4s ease" }} />
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        {tiers.map((t, i) => {
          const done = p >= t.points;
          const current = i === reached;
          const isRedeemed = redeemed.includes(t.tier);
          return (
            <div key={t.tier} style={{ flex: 1, textAlign: "center", padding: "8px 6px", borderRadius: 12,
              background: isRedeemed ? "rgba(245,158,11,0.18)" : done ? "rgba(233,30,140,0.15)" : "rgba(255,255,255,0.04)",
              border: `1px solid ${isRedeemed ? "rgba(245,158,11,0.5)" : current ? "rgba(124,58,237,0.6)" : done ? "rgba(233,30,140,0.4)" : "rgba(255,255,255,0.08)"}`,
              opacity: done || current ? 1 : 0.6 }}>
              <div style={{ fontWeight: 700, color: "#fff", fontSize: "1.02rem" }}>{isRedeemed ? "🏅 " : done ? "✓ " : ""}{t.points.toLocaleString()}</div>
              <div style={{ fontSize: ".82rem", color: "#C9CDE4", marginTop: 3, lineHeight: 1.25 }}>{t.reward}</div>
              {isRedeemed ? <div style={{ fontSize: ".7rem", color: "#f0a020", marginTop: 2, fontWeight: 700 }}>Claimed</div> : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const EditUser = ({ userObj, placeholderAvatar, setTab, onProfileUpdate, clientObj }) => {
  const { addToast } = useToast();
  const [jukeboxUserObj, setJukeboxUserObj] = useState(userObj);
  const [fileName, setFileName] = useState("");
  const [avatarImage, setAvatarImage] = useState(userObj.photoURL);
  // State to store uploaded file
  const [file, setFile] = useState("");
  // State for user requests
  const [userRequests, setUserRequests] = useState([]);
  const [groupedRequests, setGroupedRequests] = useState({});
  const [clientNames, setClientNames] = useState({});
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [points, setPoints] = useState(userObj?.points || 0);
  const [redeemedTiers, setRedeemedTiers] = useState(userObj?.redeemedTiers || []);
  const [rewardTiers, setRewardTiers] = useState(REWARD_TIERS);
  // State for personality assessment
  const [assessmentCompleted, setAssessmentCompleted] = useState(userObj.assessmentCompleted || false);
  const [assessmentData, setAssessmentData] = useState(userObj.personalityAssessment || null);

  // YouTube search state
  const [youtubeSearchTerm, setYoutubeSearchTerm] = useState('');
  const [videos, search] = useVideos('');
  const [showYoutubeResults, setShowYoutubeResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  // progress
  const [percent, setPercent] = useState(0);

  // Check if user has exceeded request rate limit
  const checkRequestRateLimit = async () => {
    if (!clientObj?.clientId) return { allowed: true };

    try {
      // Get DJ settings for rate limit
      const djDoc = await getDoc(doc(db, "users", clientObj.clientId));
      if (!djDoc.exists()) return { allowed: true };

      const userRequestRate = djDoc.data().userRequestRate || 6; // default 6 per hour

      // Get user's requests from the past hour
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
      const requestsRef = collection(db, "users", auth.currentUser.uid, "requests");
      const q = query(
        requestsRef,
        where("createdAt", ">", oneHourAgo),
        where("clientId", "==", clientObj.clientId)
      );
      const snapshot = await getDocs(q);

      if (snapshot.size >= userRequestRate) {
        return {
          allowed: false,
          message: `You've reached your limit of ${userRequestRate} requests per hour.`
        };
      }
      return { allowed: true, remaining: userRequestRate - snapshot.size };
    } catch (error) {
      console.error("Error checking rate limit:", error);
      return { allowed: true };
    }
  };

  // Send YouTube video request to DJ
  const sendYoutubeRequestToDJ = async (video) => {
    if (!clientObj?.clientId) {
      addToast("No DJ channel available");
      return;
    }

    // Check rate limit
    const rateCheck = await checkRequestRateLimit();
    if (!rateCheck.allowed) {
      addToast(rateCheck.message);
      return;
    }

    // Convert YouTube video to track format — cleaned at the source (html
    // entities, (Lyrics)/(Official Video), channel-name artists). Recording
    // descriptors like (Remix)/(Live) are kept per the 2026-08-21 ruling.
    const cleanedYt = cleanYoutubeMeta(video.snippet.title, video.snippet.channelTitle);
    const trackItem = {
      track: cleanedYt.title,
      artist: cleanedYt.artist,
      coverart: video.snippet.thumbnails.medium.url,
      youTubeId: video.id.videoId,
      // Leave genre blank — the desktop resolves the real genre when it
      // processes the request. Don't seed a placeholder.
      genre: '',
    };

    try {
      // Get user display name
      const userDocSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
      const displayName = userDocSnap.exists()
        ? userDocSnap.data().displayName
        : auth.currentUser.email;

      // Add to DJ's request queue
      const requestRef = await addDoc(
        collection(db, "users", clientObj.clientId, "djchats"),
        {
          request: trackItem,
          createdAt: serverTimestamp(),
          user: displayName,
          uid: auth.currentUser.uid,
        }
      );

      // Add to user's request history
      const historyRef = await addDoc(
        collection(db, "users", auth.currentUser.uid, "requests"),
        {
          ...trackItem,
          createdAt: serverTimestamp(),
          djchatId: requestRef.id,
          clientId: clientObj.clientId,
          channel: 'YouTube Search',
          status: 'open',
        }
      );

      // Loyalty points: +1 for this request (fire-and-forget).
      awardRequestPoint({
        uid: auth.currentUser?.uid,
        venueUid: clientObj.clientId,
        trackId: trackItem?.id || trackItem?.youTubeId || null,
      }).catch(() => {});

      // Backfill the real genre onto both the DJ request doc and the user's
      // history doc (guessGenre Cloud Function; fire-and-forget).
      backfillGenre({
        title: trackItem.track,
        artist: trackItem.artist,
        currentGenre: trackItem.genre,
        targets: [
          { ref: doc(db, "users", clientObj.clientId, "djchats", requestRef.id), field: "request.genre" },
          { ref: historyRef, field: "genre" },
        ],
      }).catch(() => {});

      // Update last request timestamp
      await setDoc(
        doc(db, "users", auth.currentUser.uid),
        { lastMuuzboxRequest: serverTimestamp() },
        { merge: true }
      );

      addToast("Request sent to DJ!");
      setYoutubeSearchTerm('');
      setShowYoutubeResults(false);
    } catch (error) {
      console.error("Error sending request:", error);
      addToast("Failed to send request");
    }
  };

  const handleYoutubeSearch = () => {
    if (!youtubeSearchTerm.trim()) return;
    setIsSearching(true);
    search(youtubeSearchTerm);
    setShowYoutubeResults(true);
    setIsSearching(false);
  };

  const handleFile = (file) => {
    setFileName(file.name);
    if (!file) {
      alert("Please upload an image first!");
    }

    const storageRef = ref(
      storage,
      `muuzbox/channels/${auth.currentUser.uid}/${file.name}`
    );

    // progress can be paused and resumed. It also exposes progress updates.
    // Receives the storage reference and the file to upload.
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const percent = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );

        // update progress
        setPercent(percent);
      },
      (err) => console.error(err),
      () => {
        // download url
        getDownloadURL(uploadTask.snapshot.ref).then((url) => {
          setAvatarImage(url);
        });
      }
    );
  };


  const handleUpload = () => {
    if (!file) {
      alert("Please upload an image first!");
    }

    const storageRef = ref(
      storage,
      `muuzbox/channels/${auth.currentUser.uid}/${file.name}`
    );

    // progress can be paused and resumed. It also exposes progress updates.
    // Receives the storage reference and the file to upload.
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const percent = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );

        // update progress
        setPercent(percent);
      },
      (err) => console.error(err),
      () => {
        // download url
        getDownloadURL(uploadTask.snapshot.ref).then((url) => {});
      }
    );
  };

  const handleEditUser = async (e) => {
    const newObj = jukeboxUserObj;

    e.target.name === "bio"
      ? (newObj.bio = e.target.value)
      : e.target.name === "newdisplayname"
      ? (newObj.displayName = e.target.value)
      : (newObj.photoURL = avatarImage);
    setJukeboxUserObj(newObj);

    setTimeout(() => {}, 500);
  };

  const handleEditUserSave = async (e) => {
    try {
      const ref = doc(db, `users`, auth.currentUser.uid);
      await updateDoc(ref, {
        displayName: jukeboxUserObj.displayName,
        photoURL: avatarImage,
        bio: jukeboxUserObj.bio || "",
        nameChanged: true,
        updatedAt: serverTimestamp(),
      });
      
      // Reload the user data from Firebase
      const updatedUserDoc = await getDoc(ref);
      if (updatedUserDoc.exists()) {
        const updatedUserData = updatedUserDoc.data();
        // Create complete updated user object
        const updatedUserObj = {
          ...jukeboxUserObj,
          ...updatedUserData,
          id: updatedUserDoc.id
        };
        
        // Update local state with fresh data from Firebase
        setJukeboxUserObj(updatedUserObj);
        
        // Send updated data back to parent component
        if (onProfileUpdate) {
          onProfileUpdate(updatedUserObj);
        }
      }
      
      // Show success toast
      addToast("Profile updated successfully!");
      
      // Navigate to requests tab if setTab is provided
      if (setTab) {
        setTimeout(() => {
          setTab("requests");
        }, 500);
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      addToast("Failed to update profile. Please try again.");
    }
  };

  const logout = async (e) => {
    await signOut(auth)
      .then(() => {
        // Sign-out successful.
        e.preventDefault();
        // console.log("signed out");
        cookies.remove("auth-token");
      })
      .catch((error) => {
        // An error happened.
        console.error("sign out error", error);
      });
  };

  const [activeIndex, setActiveIndex] = useState(null);

  // The prize archive, subscribed ONCE here: the "My prizes" section's title
  // carries the count, and its body lists the prizes, so both read this.
  const [prizeArchive, setPrizeArchive] = useState({ outstanding: [], redeemed: [] });
  useEffect(
    () => subscribePrizeArchive(auth.currentUser?.uid, setPrizeArchive),
    [auth.currentUser?.uid]
  );
  // A prize waiting to be collected is the most useful thing on this page,
  // so open its section on arrival. Once only — never fight a guest who has
  // since opened something else.
  const autoOpenedPrizes = React.useRef(false);
  useEffect(() => {
    if (autoOpenedPrizes.current || !prizeArchive.outstanding.length) return;
    autoOpenedPrizes.current = true;
    setActiveIndex((cur) => (cur === null ? "prizes" : cur));
  }, [prizeArchive.outstanding.length]);

  // Helper function to fetch client name from clientId  
  const fetchClientName = useCallback(async (clientId, namesCache) => {
    if (!clientId) return null;
    
    // Check cache first
    if (namesCache[clientId]) {
      return namesCache[clientId];
    }

    try {
      // Step 1: Get user document using clientId
      const userDoc = await getDoc(doc(db, "users", clientId));
      if (!userDoc.exists()) return null;
      
      // Step 2: Get muuzboxAccountName from user
      const muuzboxAccountName = userDoc.data().muuzboxAccountName;
      if (!muuzboxAccountName) return null;
      
      // Step 3: Get client document using muuzboxAccountName
      const clientDoc = await getDoc(doc(db, "clients", muuzboxAccountName.toLowerCase()));
      if (!clientDoc.exists()) return null;
      
      // Step 4: Get client name
      const clientName = clientDoc.data().venueInfo.name || muuzboxAccountName;
      
      return clientName;
    } catch (error) {
      console.error("Error fetching client name for", clientId, error);
      return null;
    }
  }, []);

  // Helper function to fetch djchat data (actual song request data)
  const fetchDjchatData = useCallback(async (djchatId, clientId) => {
    if (!djchatId || !clientId) return null;
    
    try {
      const djchatDoc = await getDoc(doc(db, "users", clientId, "djchats", djchatId));
      if (!djchatDoc.exists()) return null;
      
      const data = djchatDoc.data();
      // The request object contains track, artist, coverart
      return data.request || {};
    } catch (error) {
      console.error("Error fetching djchat data:", error);
      return null;
    }
  }, []);

  // Live loyalty state — points total + redeemed-tier badges — for the profile.
  useEffect(() => {
    if (!auth.currentUser) return undefined;
    const unsub = subscribeRewards(auth.currentUser.uid, ({ points: p, redeemedTiers: rt }) => {
      setPoints(p);
      setRedeemedTiers(rt);
    });
    return () => unsub && unsub();
  }, []);

  // Per-venue reward LABELS overlaid on the spec's fixed thresholds
  // (100/500/1000 stay locked to protect the economy; only the reward text is
  // venue-configurable). Reads `tierRewards` (array of label strings) or
  // `rewardTiers` (objects, .reward used) off the venue's user doc; falls back
  // to the default template.
  useEffect(() => {
    const venueUid = clientObj?.clientId;
    if (!venueUid) return;
    getDoc(doc(db, "users", venueUid))
      .then((snap) => {
        const data = snap.exists() ? snap.data() : {};
        const raw = data.tierRewards || data.rewardTiers;
        if (Array.isArray(raw) && raw.length) {
          const labels = raw
            .map((r) => (typeof r === "string" ? r : r && r.reward))
            .filter(Boolean);
          if (labels.length) {
            setRewardTiers(REWARD_TIERS.map((base, i) => ({ ...base, reward: labels[i] || base.reward })));
          }
        }
      })
      .catch(() => {});
  }, [clientObj?.clientId]);

  // Fetch user requests
  useEffect(() => {
    if (!auth.currentUser) return;

    setRequestsLoading(true);
    
    // Listen to user's requests collection
    const requestsQuery = query(
      collection(db, "users", auth.currentUser.uid, "requests"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(requestsQuery, async (snapshot) => {
      // Step 1: Collect all request references
      const requestRefs = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        requestRefs.push({ 
          id: doc.id, 
          ...data
        });
      });
      
      // Step 2: Fetch djchat data for all requests concurrently
      const requestPromises = requestRefs.map(async (requestRef) => {
        const djchatData = await fetchDjchatData(requestRef.djchatId, requestRef.clientId);
        
        // Merge djchat data with request reference
        return {
          ...requestRef,
          track: djchatData?.track || 'Unknown Track',
          artist: djchatData?.artist || 'Unknown Artist',
          coverart: djchatData?.coverart || '/default-cover.jpg'
        };
      });
      
      // Wait for all djchat data to be fetched
      const requests = await Promise.all(requestPromises);
      setUserRequests(requests);
      
      // Step 3: Group requests by clientId
      const grouped = {};
      const unknownRequests = [];
      const namesCache = {};
      
      for (const request of requests) {
        if (request.clientId) {
          if (!grouped[request.clientId]) {
            grouped[request.clientId] = [];
          }
          grouped[request.clientId].push(request);
        } else {
          unknownRequests.push(request);
        }
      }
      
      // Add unknown requests if any
      if (unknownRequests.length > 0) {
        grouped['unknown'] = unknownRequests;
      }
      
      // Step 4: Fetch client names concurrently using Promise.all
      const clientIds = Object.keys(grouped).filter(id => id !== 'unknown');
      const namePromises = clientIds.map(async (clientId) => {
        const name = await fetchClientName(clientId, namesCache);
        return { clientId, name };
      });
      
      const nameResults = await Promise.all(namePromises);
      
      // Update cache with all results
      for (const { clientId, name } of nameResults) {
        if (name) {
          namesCache[clientId] = name;
        }
      }
      
      setClientNames(namesCache);
      setGroupedRequests(grouped);
      setRequestsLoading(false);
    }, (error) => {
      console.error("Error fetching requests:", error);
      setRequestsLoading(false);
    });

    return () => unsubscribe();
  }, [fetchClientName, fetchDjchatData]);

  const handleItemClick = (index) => {
    if (index === "logout") {
      logout();
      return;
    }
    // Keep the section open when clicked again, don't toggle it closed
    setActiveIndex(index);
  };

  // Handlers for PersonalityAssessment
  const handleAssessmentComplete = async (data) => {
    try {
      setAssessmentData(data);
      setAssessmentCompleted(true);
      
      // Save assessment data to Firebase
      const ref = doc(db, `users`, auth.currentUser.uid);
      await updateDoc(ref, {
        personalityAssessment: data,
        assessmentCompleted: true,
        assessmentUpdatedAt: serverTimestamp()
      });
      
      addToast("Personality assessment completed successfully!");
    } catch (error) {
      console.error("Error saving assessment:", error);
      addToast("Failed to save assessment. Please try again.");
    }
  };

  const handleAssessmentError = (errorMessage) => {
    console.error("Assessment error:", errorMessage);
    addToast(`Assessment error: ${errorMessage}`);
  };

  const profileSections = [
    {
      id: "profile",
      title: "Profile Settings",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20,21V19a4,4,0,0,0-4-4H8a4,4,0,0,0-4,4v2" fill="none" stroke="#ffffff" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"/>
          <circle cx="12" cy="7" fill="none" r="4" stroke="#ffffff" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"/>
        </svg>
      ),
    },
    {
      id: "requests",
      title: "My Requests",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 11L12 14L22 4" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M21 12V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3H16" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
    {
      id: "prizes",
      title: prizeArchive.outstanding.length
        ? `My Prizes · ${prizeArchive.outstanding.length} to collect`
        : "My Prizes",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="8" width="18" height="4" rx="1" stroke="#ffffff" strokeWidth="2" strokeLinejoin="round"/>
          <path d="M5 12V20C5 20.5523 5.44772 21 6 21H18C18.5523 21 19 20.5523 19 20V12" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 8V21" stroke="#ffffff" strokeWidth="2" strokeLinecap="round"/>
          <path d="M12 8C12 8 11 3 8 3C6.5 3 6 4.2 6 5C6 6.5 7.5 8 12 8ZM12 8C12 8 13 3 16 3C17.5 3 18 4.2 18 5C18 6.5 16.5 8 12 8Z" stroke="#ffffff" strokeWidth="2" strokeLinejoin="round"/>
        </svg>
      ),
    },
    {
      id: "dating",
      title: "Dating Tips",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
    {
      id: "gallery",
      title: "Event Gallery",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="8.5" cy="8.5" r="1.5" fill="#ffffff"/>
          <polyline points="21 15 16 10 5 21" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
    {
      id: "settings",
      title: "App Settings",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.0113 9.77251C4.28059 9.5799 4.48572 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
  ];

  return (
    <div className="profile-container" style={{ position: "relative" }}>
      <div
        className="points-badge"
        style={{
          position: "absolute",
          top: 12,
          right: 12,
          zIndex: 5,
          display: "flex",
          alignItems: "center",
          gap: 6,
          background: "rgba(255,255,255,0.08)",
          border: "1px solid rgba(255,255,255,0.16)",
          borderRadius: 999,
          padding: "7px 13px",
          fontWeight: 700,
          color: "#fff",
          fontSize: "1.1rem",
          lineHeight: 1,
          backdropFilter: "blur(6px)",
        }}
      >
        <span role="img" aria-label="points" style={{ fontSize: "1.2rem", lineHeight: 1 }}>💎</span>
        {Number(points || 0).toLocaleString()}
      </div>
      <div className="profile-header">
        <div className="profile-avatar-section">
          <div className="profile-avatar">
            <img
              src={avatarImage || placeholderAvatar}
              alt="Profile"
              className="profile-avatar-img"
            />
          </div>
          <h2 className="profile-name">{userObj.displayName}</h2>
          <p className="profile-type">Premium Member</p>
          <p className="hidden">{userObj.id}</p>
        </div>
      </div>

      <TierProgress points={points} tiers={rewardTiers} redeemed={redeemedTiers} />

      {/* Redeem QR — appears when the guest has reached a tier they haven't
          claimed yet. Venue staff scan it to award that tier's BADGE; points
          are NOT deducted (they keep climbing to the next tier). */}
      {(() => {
        const redeemable = [...rewardTiers].reverse().find(
          (t) => Number(points) >= t.points && !redeemedTiers.includes(t.tier)
        );
        if (!redeemable || !auth.currentUser?.uid) return null;
        return (
          <div style={{ margin: "0 16px 16px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 16, padding: 16, textAlign: "center" }}>
            <div style={{ fontWeight: 700, color: "#fff", fontSize: "1rem", marginBottom: 4 }}>Claim your Tier {redeemable.tier} reward</div>
            <div style={{ fontSize: ".85rem", color: "#9AA0C0", marginBottom: 12 }}>
              Show this to venue staff to claim <strong>{redeemable.reward}</strong>. Your points stay — you keep earning toward the next tier.
            </div>
            <div style={{ display: "inline-block", background: "#fff", padding: 12, borderRadius: 12 }}>
              <QRCodeSVG
                value={JSON.stringify({ typ: "muuzbox-redeem", uid: auth.currentUser.uid, v: 1 })}
                size={176}
                level="M"
              />
            </div>
            <div style={{ fontSize: ".65rem", color: "#6b7280", marginTop: 8, wordBreak: "break-all" }}>
              ID: {auth.currentUser.uid}
            </div>
          </div>
        );
      })()}

      <div className="accordion-list">
        {profileSections.map((section) => (
          <AccordionItem
            key={section.id}
            title={section.title}
            icon={section.icon}
            isOpen={activeIndex === section.id}
            onClick={() => handleItemClick(section.id)}
            sectionId={section.id}
          >
            {section.id === "profile" ? (
              <div className="profile-settings-content">
                <div className="profile-form">
                  <div className="form-group">
                    <label>Display Name</label>
                    <input
                      type="text"
                      name="newdisplayname"
                      defaultValue={userObj.displayName}
                      onChange={handleEditUser}
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Bio</label>
                    <textarea
                      name="bio"
                      defaultValue={jukeboxUserObj.bio || ""}
                      onChange={handleEditUser}
                      className="form-textarea"
                      rows={3}
                    />
                  </div>

                  <div className="form-group">
                    <label>Profile Picture</label>
                    <FileUploader 
                      handleFile={handleFile}
                      placeholderAvatar={placeholderAvatar}
                      avatarImage={avatarImage}
                    />
                    {percent > 0 && <p className="upload-progress">Upload Progress: {percent}%</p>}
                  </div>

                  <button
                    onClick={handleEditUserSave}
                    className="save-button"
                  >
                    Save Changes
                  </button>
                </div>

                <div className="social-links">
                  <LinkToGoogle />
                </div>
              </div>
            ) : section.id === "prizes" ? (
              <PrizeArchive uid={auth.currentUser?.uid} archive={prizeArchive} />
            ) : section.id === "requests" ? (
              <div className="requests-content">
                {/* YouTube Search Section */}
                <div className="youtube-search-section" style={{
                  marginBottom: '1.5rem',
                  padding: '1rem',
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: '12px'
                }}>
                  <h3 style={{
                    color: '#fff',
                    marginBottom: '0.75rem',
                    fontSize: '1rem',
                    fontWeight: '600'
                  }}>
                    Request a Song from Muuzbox Internet
                  </h3>

                  {/* Search Input */}
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <input
                      type="text"
                      placeholder="Search for a song..."
                      value={youtubeSearchTerm}
                      onChange={(e) => setYoutubeSearchTerm(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleYoutubeSearch();
                      }}
                      style={{
                        flex: 1,
                        padding: '0.75rem 1rem',
                        borderRadius: '8px',
                        border: '1px solid rgba(255,255,255,0.2)',
                        background: 'rgba(0,0,0,0.3)',
                        color: '#fff',
                        fontSize: '1rem',
                      }}
                    />
                    <button
                      onClick={handleYoutubeSearch}
                      disabled={isSearching}
                      style={{
                        padding: '0.75rem 1.5rem',
                        borderRadius: '8px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: '#fff',
                        border: 'none',
                        cursor: 'pointer',
                        fontWeight: '600',
                      }}
                    >
                      {isSearching ? '...' : 'Search'}
                    </button>
                  </div>

                  {/* Search Results */}
                  {showYoutubeResults && videos.length > 0 && (
                    <div style={{
                      maxHeight: '300px',
                      overflowY: 'auto',
                      marginTop: '0.75rem',
                    }}>
                      {videos.map((video) => (
                        <div
                          key={video.id.videoId}
                          onClick={() => sendYoutubeRequestToDJ(video)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            padding: '0.5rem',
                            cursor: 'pointer',
                            borderRadius: '8px',
                            marginBottom: '0.5rem',
                            background: 'rgba(255,255,255,0.05)',
                            transition: 'background 0.2s',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                        >
                          <img
                            src={video.snippet.thumbnails.default.url}
                            alt=""
                            style={{
                              width: '60px',
                              height: '45px',
                              borderRadius: '4px',
                              objectFit: 'cover'
                            }}
                          />
                          <div style={{ flex: 1, overflow: 'hidden' }}>
                            <p style={{
                              color: '#fff',
                              fontSize: '0.9rem',
                              margin: 0,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {video.snippet.title}
                            </p>
                            <p style={{
                              color: 'rgba(255,255,255,0.6)',
                              fontSize: '0.8rem',
                              margin: 0
                            }}>
                              {video.snippet.channelTitle}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {showYoutubeResults && videos.length === 0 && youtubeSearchTerm && (
                    <p style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', marginTop: '1rem' }}>
                      No results found
                    </p>
                  )}
                </div>

                {/* Existing requests list */}
                {requestsLoading ? (
                  <div className="loading-state">
                    <p>Loading your requests...</p>
                  </div>
                ) : userRequests.length > 0 ? (
                  <div className="grouped-requests">
                    {Object.entries(groupedRequests).map(([clientId, clientRequests]) => (
                      <div key={clientId} className="client-request-group" style={{ marginBottom: '2rem' }}>
                        <h3 className="client-name-header" style={{
                          fontSize: '1.2rem',
                          fontWeight: 'bold',
                          color: '#fff',
                          marginBottom: '1rem',
                          paddingBottom: '0.5rem',
                          borderBottom: '1px solid rgba(255,255,255,0.2)'
                        }}>
                          {clientId === 'unknown' ? 'Unknown Requests' : (clientNames[clientId] || 'Loading...')}
                        </h3>
                        <ul className="requests-list divide-y divide-gray-700">
                          {clientRequests.map((request, index) => (
                            <RequestItem key={request.id} request={request} index={index} />
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <p>You haven't made any song requests yet.</p>
                    <p className="empty-state-hint">Request songs from the DJ's playlist!</p>
                  </div>
                )}
              </div>
            ) : section.id === "dating" ? (
              <div className="dating-tips-container">
                <ConversationEngine
                  userId={userObj.id}
                  userProfile={{
                    displayName: userObj.displayName,
                    bio: jukeboxUserObj.bio,
                    avatar: avatarImage,
                    personalityAssessment: assessmentData,
                    assessmentCompleted: assessmentCompleted
                  }}
                  onProfileUpdate={(updates) => {
                    if (updates.personalityAssessment) {
                      setAssessmentData(updates.personalityAssessment);
                      setAssessmentCompleted(true);
                      handleAssessmentComplete(updates.personalityAssessment);
                    }
                  }}
                />
              </div>
            ) : section.id === "gallery" ? (
              <div className="gallery-content">
                <EventGallery userId={auth.currentUser?.uid} />
              </div>
            ) : (
              <div className="placeholder-content">
                <p>This feature is coming soon!</p>
              </div>
            )}
          </AccordionItem>
        ))}

        <AccordionItem
          title="Log Out"
          icon={
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="#ff0000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <polyline points="16 17 21 12 16 7" stroke="#ff0000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="21" y1="12" x2="9" y2="12" stroke="#ff0000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          isLogout={true}
          onClick={() => handleItemClick("logout")}
        />
      </div>
    </div>
  );
};

export default EditUser;
